import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Smile, Upload, Trash2, Plus } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

export default function Emojis() {
  const { guildId } = useGuild();
  const [emojis, setEmojis] = useState(null);
  const [name, setName] = useState("");
  const [dataUri, setDataUri] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/guilds/${guildId}/emojis`).then((r) => setEmojis(r.data)).catch(() => setEmojis([]));
  useEffect(() => { load(); }, [guildId]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 256 * 1024) { toast.error("Emoji must be under 256KB (Discord limit)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setDataUri(reader.result); if (!name) setName(f.name.split(".")[0].replace(/[^a-zA-Z0-9_]/g, "")); };
    reader.readAsDataURL(f);
  };

  const create = async () => {
    if (!dataUri || !name) { toast.error("Pick an image and enter a name"); return; }
    setBusy(true);
    try {
      await api.post(`/guilds/${guildId}/emojis`, { name, image: dataUri });
      toast.success(`:${name}: added to your server`);
      setName(""); setDataUri(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add emoji"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/guilds/${guildId}/emojis/${id}`);
    toast.success("Emoji removed");
    load();
  };

  return (
    <div>
      <PageHead title="Emojis" desc="Add and manage custom emojis on your Discord server." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
          <h3 className="font-head font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-volt" /> Create custom emoji</h3>
          <div onClick={() => fileRef.current?.click()} data-testid="emoji-dropzone"
            className="border-2 border-dashed border-zinc-700 rounded-xl p-6 text-center cursor-pointer hover:border-volt transition-colors">
            {dataUri ? <img src={dataUri} alt="" className="w-16 h-16 mx-auto object-contain" />
              : <><Upload className="w-6 h-6 mx-auto text-zinc-500 mb-2" /><span className="text-sm text-zinc-500">Click to upload (PNG/GIF, &lt;256KB)</span></>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={pick} className="hidden" data-testid="emoji-file" />
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Emoji name</label>
            <input data-testid="emoji-name" value={name} onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="my_emoji"
              className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
          </div>
          <button data-testid="emoji-create" onClick={create} disabled={busy}
            className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover disabled:opacity-60">
            {busy ? "Adding…" : "Add to server"}
          </button>
        </div>

        <div className="lg:col-span-2">
          {emojis === null ? <p className="text-zinc-500">Loading…</p> : emojis.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl">
              <Smile className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
              No custom emojis yet. Upload one on the left to get started.
              <p className="text-xs text-zinc-600 mt-2">(This server must be linked to your live bot.)</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="emoji-grid">
              {emojis.map((e) => (
                <div key={e.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex flex-col items-center gap-2 group relative">
                  <img src={e.url} alt={e.name} className="w-12 h-12 object-contain" />
                  <span className="text-xs text-zinc-400 truncate w-full text-center">:{e.name}:</span>
                  <button onClick={() => del(e.id)} data-testid={`emoji-delete-${e.name}`}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500/20 text-red-400 rounded p-1 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
